// plugin-loader.js - discovers, validates, and mounts GDBS plugins per tier.
// Implements docs/PLUGIN_SPEC.txt section 6. Self-initializing ES module; degrades
// gracefully (a missing index.json / bad plugin / offline api is a console warning,
// never a UI break). Mounts:
//   std -> a Standard-tier view + sidebar nav item
//   pro -> a Pro-tier view + sidebar nav item (entitlement-gated)
//   hpc -> an HPC Lab tab + panel
// and registers each plugin's dev-log/bug-log/trust into window.GDBS_ENGINE_LOG.

import init from './engines.js';
import { createHost, h, cancelEngine } from './plugin-host.js';
import * as market from './plugin-market.js';
import { getUser } from './auth.js';
import { createSandbox } from './plugin-sandbox.js';
import { mountVerifyBadge } from './verify-badge.js';

const TIERS = ['std', 'pro', 'hpc'];
const mounted = new Set();
let activeTag = null;
let searchQuery = '';
let currentTier = null; // catalog is scoped to the tier whose nav opened it (null = all)
let catalogIsAdmin = false; // set once the admin probe confirms; reveals per-card manage controls

// Canonical tier colours (match the STD/PRO/HPC tier-dots in app.css).
const TIER_COLOR = { std: '#6b7280', pro: '#f59e0b', hpc: '#10b981' };
// Trust levels 0..5 -> { label, colour, icon }. red, orange, yellow, teal, green, blue.
const TRUST = [
  { label: 'Unverified', color: '#94a3b8', icon: 'fa-circle-question' }, // auto-screened, not yet community-rated
  { label: 'Untrusted', color: '#f97316', icon: 'fa-triangle-exclamation' },
  { label: 'Poor', color: '#eab308', icon: 'fa-circle' },
  { label: 'Average', color: '#14b8a6', icon: 'fa-circle-half-stroke' },
  { label: 'Trusted', color: '#22c55e', icon: 'fa-circle-check' },
  { label: 'Excellent', color: '#3b82f6', icon: 'fa-circle-check' },
];
function isVss(m) { return m._origin === 'gdbs' || m._author === 'VaultSync Solutions'; }
// "VSS endorsed" = first-party VSS plugins OR community plugins an admin granted VSS Approved;
// these sort to the TOP of the catalog, ahead of everything ranked by rating.
function isVssEndorsed(m) { return m._origin === 'gdbs' || !!m._vssApproved; }
// VSS products are floored at Trusted (4); Excellent (5) only if avg rounds to 5; never lower.
// Community plugins earn trust from reviews: 0 reviews = No Trust, else avg star -> level.
function trustLevelOf(m) {
  const count = m._ratingCount || 0, avg = +m._rating || 0;
  if (isVss(m)) return (count > 0 && Math.round(avg) >= 5) ? 5 : 4;
  if (count === 0) return 0;
  return Math.max(1, Math.min(5, Math.round(avg)));
}
function trustBadgeEl(m) {
  const t = TRUST[trustLevelOf(m)], count = m._ratingCount || 0, avg = Number(m._rating || 0).toFixed(1);
  const tip = isVss(m)
    ? ('VaultSync Solutions product - trusted by default' + (count ? ' (' + avg + ' stars from ' + count + ')' : ''))
    : (count ? ('Community: ' + avg + ' stars from ' + count + ' review' + (count === 1 ? '' : 's')) : 'Auto-screened (security + sandboxed dry-run), not yet community-rated');
  return h('span', { title: tip, style: { position: 'absolute', top: '8px', right: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px',
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.02em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '9999px',
    color: t.color, background: t.color + '22', border: '1px solid ' + t.color + '88', whiteSpace: 'nowrap', pointerEvents: 'none' } },
    h('i', { class: 'fas ' + t.icon }), t.label);
}

// ---------- discovery ----------
async function discover(tier) {
  // 1) backend auto-discovery (optional), 2) static manifest (always FTP-deployable)
  try {
    const r = await fetch('/api/plugins/' + tier, { headers: { Accept: 'application/json' } });
    if (r.ok) { const j = await r.json(); if (j && Array.isArray(j.plugins)) return j.plugins; }
  } catch (e) { /* fall through */ }
  try {
    const r = await fetch('/plugins/' + tier + '/index.json', { cache: 'no-cache' });
    if (r.ok) { const j = await r.json(); if (j && Array.isArray(j.plugins)) return j.plugins; }
  } catch (e) { /* none */ }
  return [];
}

// per-tier CARD metadata from the static catalog (plugins/{tier}/index.json), so
// cards render WITHOUT importing (executing) any plugin code. Fetched once per tier.
const _tierMeta = {};
async function tierMeta(tier) {
  if (!_tierMeta[tier]) _tierMeta[tier] = (async () => {
    const map = new Map();
    try {
      const r = await fetch('/plugins/' + tier + '/index.json', { cache: 'no-cache' });
      if (r.ok) { const j = await r.json(); if (j && Array.isArray(j.plugins)) for (const p of j.plugins) if (p && typeof p === 'object') { if (p.file) map.set(p.file, p); if (p.id) map.set(p.id, p); } }
    } catch (e) { /* none */ }
    return map;
  })();
  return _tierMeta[tier];
}

// Materialize a plugin's EXECUTABLE manifest. .es -> import the module (blob-import,
// with a same-URL fallback for absolute imports); .gdb -> fetch the declarative JSON.
// This is the ONLY place plugin code is executed, and it runs lazily (on open of an
// entitled plugin - see ensurePluginCode), never at boot.
// Blob-import a plugin's source STRING as an ES module (JS mime so the custom .es loads even if the
// server labels it non-JS), returning its default export (the manifest). Used for both the per-file
// fetch and the one-pack (id -> code) entries.
// A blob: URL has an OPAQUE, non-hierarchical base, so a module loaded from one cannot resolve
// ANY path specifier - not even a root-absolute '/js/geo-num.js' (the loader throws
// "Invalid relative url or base scheme isn't hierarchical" at LINK time, before the module runs).
// Since gated delivery moved every built-in plugin to blob-import, that broke every plugin
// carrying a shared-module import: the throw surfaced as ensurePluginCode() -> false ->
// the read-only kiosk "sign in with an entitled account" panel, with entitlement never consulted.
// Origin-qualifying the specifier restores a hierarchical base and resolves it correctly.
// Anchored on `from '...'` / `import '...'` / `import('...')` so only real module specifiers are
// touched - a plain string literal like '/api/compute' inside the plugin body is left alone.
const ABS_SPEC = /(\bfrom\s*|\bimport\s*\(\s*|\bimport\s*)(['"])(\/(?!\/)[^'"]*)\2/g;
export function qualifyPluginImports(src, origin) {
  return src.replace(ABS_SPEC, (_m, lead, q, spec) => lead + q + origin + spec + q);
}
async function importPluginSrc(src) {
  const origin = (typeof location !== 'undefined' && location.origin) ? location.origin : '';
  const burl = URL.createObjectURL(new Blob([qualifyPluginImports(src, origin)], { type: 'text/javascript' }));
  try { const mod = await import(/* @vite-ignore */ burl); return mod.default || mod; }
  finally { URL.revokeObjectURL(burl); }
}

async function fetchPluginManifest(url, isGdb, headers) {
  const opts = { cache: 'no-cache' };
  if (headers && Object.keys(headers).length) opts.headers = headers;
  if (isGdb) {
    const r = await fetch(url, opts);
    if (!r.ok) { const e = new Error('fetch ' + url + ' -> ' + r.status); e.status = r.status; throw e; }
    const m = await r.json(); m._declarative = true; return m;
  }
  const r = await fetch(url, opts);
  if (!r.ok) { const e = new Error('fetch ' + url + ' -> ' + r.status); e.status = r.status; throw e; }
  const src = await r.text();
  try { return await importPluginSrc(src); }
  catch (e) {
    // A same-URL import fallback is only safe for an UNAUTHENTICATED (static) fetch - a gated URL's
    // bare import() cannot carry the Authorization header and would just 401.
    if (opts.headers) throw e;
    const mod = await import(/* @vite-ignore */ url);
    return mod.default || mod;
  }
}

// One-pack cache: tier -> Promise<{ id: codeSrc }>. The FIRST open of a plugin in a tier fetches
// the whole entitled tier in one request; later opens hit the cache (no fetch). App path only
// (JWT), so it carries the Authorization header. A failed fetch is not cached (retryable).
const _tierPack = {};
function ensureTierPack(tier, token) {
  if (!_tierPack[tier]) {
    _tierPack[tier] = (async () => {
      const r = await fetch('/api/plugin-code/pack/' + encodeURIComponent(tier), { cache: 'no-cache', headers: { Authorization: 'Bearer ' + token } });
      if (!r.ok) { const e = new Error('pack ' + r.status); e.status = r.status; throw e; }
      return await r.json();
    })().catch((e) => { delete _tierPack[tier]; throw e; });
  }
  return _tierPack[tier];
}

// RUNTIME authorization: fetch the entitlement-bound run key at run time. Returns the key string
// (attached to the host so a plugin can fold it into its result), or null for a non-gated
// (.gdb/external) plugin. THROWS on 401/403/410 - the run is not authorized. A scraped copy run
// outside the app cannot obtain this (no valid token/share), so it cannot reproduce a valid result.
// Cached per manifest for the session (fetched once with a valid token; the key is deterministic).
async function fetchRuntimeKey(m) {
  if (!m || m._gdb || (m.origin && m.origin !== 'gdbs') || !m.tier || !m.id) return null;
  if (m._runtimeKey) return m._runtimeKey;
  const kiosk = (typeof window !== 'undefined') ? window.__kioskShareToken : null;
  let url = '/api/plugin-code/runtime-key/' + encodeURIComponent(m.tier) + '/' + encodeURIComponent(m.id);
  const headers = {};
  if (kiosk) url += '?share=' + encodeURIComponent(kiosk);
  else { const t = readToken(); if (t) headers.Authorization = 'Bearer ' + t; }
  const r = await fetch(url, { cache: 'no-cache', headers });
  if (!r.ok) { const e = new Error('runtime-key ' + r.status); e.status = r.status; throw e; }
  const j = await r.json();
  m._runtimeKey = (j && j.key) || null;
  return m._runtimeKey;
}

// Build the GATED code request for a built-in plugin: the server endpoint + the authorizer
// (a kiosk share token if present, else the app JWT). Returns null for a .gdb/declarative or an
// external (non-gdbs) plugin, which keep their own _sourceUrl. Pure, so it is unit-tested.
export function pluginCodeRequest(m, ctx) {
  ctx = ctx || {};
  if (!m || m._gdb) return null;                                  // declarative .gdb keeps _sourceUrl
  if (m.origin && m.origin !== 'gdbs') return null;              // user/external plugin
  if (!m.tier || !m.id) return null;
  const base = '/api/plugin-code/' + encodeURIComponent(m.tier) + '/' + encodeURIComponent(m.id);
  if (ctx.kioskShareToken) return { url: base + '?share=' + encodeURIComponent(ctx.kioskShareToken), headers: {} };
  const headers = {};
  if (ctx.token) headers.Authorization = 'Bearer ' + ctx.token;
  return { url: base, headers };
}

// Denial (locked) vs a transient error: on 401/403/410 the server has SPOKEN - do not fall back
// to the static file (that would defeat the gate). Only a network error / 404 (endpoint absent,
// e.g. mid-rollout) is a reason to try the legacy static path.
function isDenial(err) { return err && (err.status === 401 || err.status === 403 || err.status === 410); }
function readToken() { try { return localStorage.getItem('gdbs_token'); } catch (_) { return null; } }

// Sibling-plugin SOURCE for COMPOSING plugins (a resolver that wires in other plugins' real exports).
// Direct /plugins/*.plugin.es fetches are 403'd by the static-code gate, so a composing plugin must
// obtain its bricks through the SAME gated path the loader uses. Returns raw JS source text (the
// caller blob-imports it). Prefers the already-fetched entitled tier pack (no extra request), then a
// per-plugin gated fetch (kiosk share token in kiosk view, else the app JWT). Exposed as a window
// global so a blob-imported plugin - which has no module access to these internals - can call it.
async function gatedPluginSource(tier, id) {
  const kiosk = (typeof window !== 'undefined') ? window.__kioskShareToken : null;
  const token = readToken();
  if (!kiosk && token && _tierPack[tier]) {
    try { const pack = await _tierPack[tier]; if (pack && typeof pack[id] === 'string') return pack[id]; }
    catch (_) { /* pack failed/absent -> fall through to a direct gated fetch */ }
  }
  let url = '/api/plugin-code/' + encodeURIComponent(tier) + '/' + encodeURIComponent(id);
  const headers = {};
  if (kiosk) url += '?share=' + encodeURIComponent(kiosk);
  else if (token) headers.Authorization = 'Bearer ' + token;
  const r = await fetch(url, { cache: 'no-cache', headers });
  if (!r.ok) { const e = new Error('plugin-code ' + tier + '/' + id + ' -> ' + r.status); e.status = r.status; throw e; }
  return await r.text();
}
try { if (typeof window !== 'undefined') window.__gdbsPluginSource = gatedPluginSource; } catch (_) {}

// entry: a string filename (legacy index.json) OR an object from /api/plugins/{tier}
// or the enriched index.json ({file?, id?, source?, origin, name?, icon?, blurb?,
// tags?, rating?, ratingCount?, format?}). Built-in plugins resolve to a LAZY card
// manifest from catalog metadata (NO code executed); their code loads on open.
async function loadManifest(tier, entry) {
  const obj = (typeof entry === 'string') ? { file: entry, origin: 'gdbs' } : (entry || {});
  const isGdb = obj.format === 'gdb' || /\.gdb(\.json)?$/i.test(obj.file || '');
  // uploaded (approved) plugins are served from the API; built-ins from /plugins/.
  const url = obj.source ? obj.source : ('/plugins/' + tier + '/' + obj.file);
  let m;
  if (!isGdb && obj.origin === 'user') {
    // THIRD-PARTY CODE: never imported into the main realm. Build a lightweight manifest
    // from the server's submission metadata; the code runs ONLY in the sandbox worker
    // (see openPlugin -> initSandbox / plugin-sandbox.js). The form schema (inputs/presets)
    // is fetched from the worker at open time.
    m = { id: obj.id || obj.file || 'plugin', tier: tier, name: obj.name || obj.file || 'plugin',
      icon: obj.icon || 'fa-puzzle-piece', accent: obj.accent || '#60a5fa', blurb: obj.blurb || '',
      references: [], _sandboxed: true, _sourceUrl: url };
  } else {
    // BUILT-IN (.es or .gdb): build the card from catalog metadata WITHOUT importing.
    // The executable surface (run/render/inputs/engine) is materialized lazily on open
    // (ensurePluginCode), so no plugin code is downloaded/executed for the catalog.
    const meta = (obj.name || obj.icon) ? obj : (await tierMeta(tier)).get(obj.file);
    if (meta && meta.id) {
      m = { id: meta.id, tier: tier, name: meta.name || meta.id, icon: meta.icon || 'fa-puzzle-piece',
        accent: meta.accent || '#60a5fa', blurb: meta.blurb || '', tags: Array.isArray(meta.tags) ? meta.tags : [],
        references: meta.references || [], version: meta.version || '',
        _lazy: true, _gdb: isGdb, _sourceUrl: url, _file: obj.file, _hasRender: !!meta.hasRender };
      if (meta.requiredModule) m.requiredModule = meta.requiredModule;
    } else {
      // back-compat (catalog miss, e.g. a new file before the generator re-runs): load
      // now so the card still appears. The common path is the lazy branch above.
      m = await fetchPluginManifest(url, isGdb);
    }
  }
  // overlay marketplace metadata (origin badge, rating, the rating/plugin id)
  m._origin = obj.origin || 'gdbs';
  m._id = obj.id || ('gdbs:' + tier + '/' + (obj.file || m.id));
  m._rating = obj.rating || 0;
  m._ratingCount = obj.ratingCount || 0;
  if (obj.submitter) m._submitter = obj.submitter;
  // author: built-in GDBS plugins are by VaultSync Solutions; uploaded plugins are by their
  // submitter. A manifest may override with its own `author`.
  m._author = m.author || (m._origin === 'gdbs' ? 'VaultSync Solutions' : (obj.submitter || m._submitter || 'Community'));
  m._vssApproved = !!obj.vssApproved; // admin "VSS Approved" quality blessing (community plugins)
  // Catalog `added` date -> drives the "New" badge. The plugin LIST often comes from
  // /api/plugins, whose entries omit `added`, so fall back to the static index.json
  // metadata (tierMeta), keyed by file or id, as the authoritative source.
  m._added = obj.added || m.added || '';
  if (!m._added) {
    try { const _cm = await tierMeta(tier); const _cat = _cm.get(obj.file) || _cm.get(obj.id) || _cm.get(m.id); if (_cat && _cat.added) m._added = _cat.added; } catch (e) { /* keep empty -> simply not flagged new */ }
  }
  // discovery/topic tags: server-provided (uploads) merged with the manifest's own `tags`
  // array (built-ins declare them in the manifest). Lowercased, deduped, capped.
  { const t = new Set([...(obj.tags || []), ...(Array.isArray(m.tags) ? m.tags : [])].map(x => String(x).toLowerCase().trim()).filter(Boolean)); m._tags = [...t].slice(0, 12); }
  return m;
}

function valid(m, tier) {
  return m && typeof m.id === 'string' && m.tier === tier &&
    (typeof m.run === 'function' || (m._declarative && m.engine) || m._sandboxed || m._lazy);
}

// Materialize a lazy built-in plugin's code on demand. Returns false (WITHOUT fetching
// any code) when the caller isn't entitled - so a Pro/HPC plugin's source is never
// downloaded by a user who can't run it. On success the executable surface (run/render/
// inputs/engine/...) is merged onto the lazy manifest.
async function ensurePluginCode(m) {
  if (!m || !m._lazy || m._codeLoaded) return true;
  if (!entitled(m)) return false;

  // Prefer the GATED endpoint (server enforces the license/share/demo authorizer + emits the
  // code). Fall back to the legacy static _sourceUrl ONLY on a network error or 404 (endpoint not
  // yet deployed) - a 401/403/410 is a real denial and must lock, not silently serve the static
  // file. Non-built-in (.gdb / external) plugins have no gated request and use _sourceUrl directly.
  const req = pluginCodeRequest(m, { token: readToken(), kioskShareToken: (typeof window !== 'undefined' ? window.__kioskShareToken : null) });
  let real;
  if (req) {
    // App path (JWT, not kiosk): try the one-pack first - one request loads the whole entitled
    // tier, then later opens in that tier are cache hits. Fall through to the per-plugin gated
    // fetch if the pack is unavailable or does not include this id.
    const token = readToken();
    const isApp = !!(req.headers && req.headers.Authorization) && !(typeof window !== 'undefined' && window.__kioskShareToken);
    if (isApp && token) {
      try {
        const pack = await ensureTierPack(m.tier, token);
        if (pack && typeof pack[m.id] === 'string') real = await importPluginSrc(pack[m.id]);
      } catch (e) { /* pack unavailable -> per-plugin gated fetch below */ }
    }
    // ENFORCED: the gated endpoint is the only door. Any failure (denied token, decrypt/encrypt
    // error, network) returns false - the caller shows the read-only kiosk view, never a 404 and
    // never the (now-blocked) static file. No static fallback.
    if (!real) {
      try { real = await fetchPluginManifest(req.url, m._gdb, req.headers); }
      catch (e) { return false; }
    }
  } else {
    // Non-gated: declarative .gdb / external plugins keep their own source (not *.plugin.es).
    real = await fetchPluginManifest(m._sourceUrl, m._gdb);
  }
  for (const k of ['run', 'render', 'inputs', 'presets', 'engine', 'map', 'outputs', 'devlog', 'buglog', 'trust', 'author', 'version']) {
    if (real[k] != null) m[k] = real[k];
  }
  if (real._declarative) m._declarative = true;
  if (Array.isArray(real.references) && real.references.length) m.references = real.references;
  m._codeLoaded = true;
  return true;
}

// ---------- entitlement (soft gate; std is open; permissive when tier unknown) ----------
function getLic() {
  try { if (window.gdbsGetLicense) return window.gdbsGetLicense(); } catch (e) {}
  try { if (typeof window.__gdbsLicense !== 'undefined') return window.__gdbsLicense; } catch (e) {}
  return null;
}
function entitled(m) {
  const lic = getLic();
  const tier = (lic && lic.tier) || null;
  const mods = (lic && lic.modules) || [];
  if (!tier) return true; // unknown -> do not block the integration
  if (['admin', 'research', 'enterprise'].indexOf(tier) !== -1) return true;
  if (m.requiredModule) return mods.indexOf(m.requiredModule) !== -1;
  if (m.tier === 'std') return true;
  if (m.tier === 'pro') return ['pro', 'premium'].indexOf(tier) !== -1 || mods.length > 0 || (lic && lic.isActive);
  if (m.tier === 'hpc') return mods.indexOf('hpclab') !== -1 || ['pro', 'premium'].indexOf(tier) !== -1;
  return true;
}

// ---------- form ----------
function buildForm(panel, m) {
  const inputs = m.inputs || [];
  const form = h('form', { class: 'plugin-form', style: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', margin: '10px 0' } });
  // run() validates/coerces inputs itself. Disable HTML5 constraint validation:
  // a step-mismatched value (e.g. a real cal-kit coefficient like 49.433 in a
  // step=0.1 field) that lives in a COLLAPSED advanced panel is "invalid and not
  // focusable", which silently blocks submit. (Caught on prod with the 85033E preset.)
  form.noValidate = true;
  if (m.presets && Object.keys(m.presets).length) {
    const sel = h('select', { class: 'form-control', style: { width: 'auto' } },
      h('option', { value: '' }, 'Preset...'),
      ...Object.keys(m.presets).map(n => h('option', { value: n }, n)));
    sel.addEventListener('change', () => {
      const p = m.presets[sel.value]; if (!p) return;
      for (const k in p) { const f = form.querySelector('[name="' + k + '"]'); if (f) { if (f.type === 'checkbox') f.checked = !!p[k]; else f.value = p[k]; } }
    });
    form.appendChild(h('label', { style: { fontSize: '12px', color: '#888' } }, 'Preset', sel));
  }
  const mkField = (inp) => {
    let field;
    if (inp.type === 'select') {
      field = h('select', { name: inp.key, class: 'form-control', style: { width: 'auto' } },
        ...(inp.options || []).map(o => h('option', { value: o.v }, o.l)));
      field.value = inp.default != null ? inp.default : '';
    } else if (inp.type === 'checkbox') {
      field = h('input', { name: inp.key, type: 'checkbox' });
      if (inp.default) field.checked = true;
    } else if (inp.type === 'file') {
      // File upload (CSV / list / txt). The file is read to text on selection and
      // stashed on the element so collectParams() can stay synchronous; the
      // plugin's run() receives the file's TEXT content as the input value.
      const fileInput = h('input', { name: inp.key, type: 'file', class: 'form-control', style: { width: 'auto' } });
      if (inp.accept) fileInput.accept = inp.accept;
      fileInput.__fileText = '';
      fileInput.addEventListener('change', () => {
        const file = fileInput.files && fileInput.files[0];
        if (!file) { fileInput.__fileText = ''; return; }
        const reader = new FileReader();
        reader.onload = () => { fileInput.__fileText = String(reader.result || ''); };
        reader.onerror = () => { fileInput.__fileText = ''; };
        reader.readAsText(file);
      });
      // Optional "Download template" - an input may declare a sample file so the
      // user can see the expected format, fill it in, and upload it back.
      if (inp.template && inp.template.content) {
        const dl = h('a', { href: '#', style: { fontSize: '11px', color: '#7c3aed', whiteSpace: 'nowrap', textDecoration: 'underline' } }, 'Download template');
        dl.addEventListener('click', (e) => {
          e.preventDefault();
          const t = inp.template;
          const blob = new Blob([t.content], { type: t.mime || 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = h('a', { href: url, download: t.filename || 'template.csv' });
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        });
        field = h('span', { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, fileInput, dl);
      } else {
        field = fileInput;
      }
    } else if (inp.type === 'textarea') {
      // Multi-line paste (CSV / row-based data). A single-line <input> SILENTLY
      // strips newline characters, collapsing row input to one line - so any plugin
      // taking pasted rows must declare type:'textarea', which preserves newlines.
      field = h('textarea', { name: inp.key, class: 'form-control', rows: inp.rows || 6,
        style: { width: '100%', minWidth: '320px', fontFamily: 'ui-monospace,monospace', fontSize: '12px', lineHeight: '1.45', resize: 'vertical' } });
      if (inp.default != null) field.value = inp.default;
    } else {
      // Map declared type to a real HTML input type. `date` gets the NATIVE date picker (so mobile
      // shows a calendar wheel and never a raw text field the user has to type YYYY-MM-DD into);
      // its value is already a YYYY-MM-DD string, which collectParams passes straight through.
      const nativeType = inp.type === 'number' ? 'number' : (inp.type === 'date' ? 'date' : 'text');
      const width = inp.type === 'number' ? '110px' : (inp.type === 'date' ? '170px' : '160px');
      field = h('input', { name: inp.key, type: nativeType, class: 'form-control', style: { width } });
      if (inp.min != null) field.min = inp.min; if (inp.max != null) field.max = inp.max; if (inp.step != null) field.step = inp.step;
      if (inp.default != null) field.value = inp.default;
    }
    // A textarea spans the full form row (a narrow column would defeat multi-line input).
    const labelStyle = { fontSize: '12px', color: '#888', display: 'flex', flexDirection: 'column', gap: '3px' };
    if (inp.type === 'textarea') labelStyle.width = '100%';
    return h('label', { style: labelStyle, title: inp.help || '' }, inp.label || inp.key, field);
  };
  // Basic inputs inline; inputs flagged `advanced:true` go in a collapsible
  // "Advanced" panel (defaults intact, clean by default - deep when opened).
  const adv = [];
  for (const inp of inputs) {
    if (inp.advanced) { adv.push(inp); continue; }
    form.appendChild(mkField(inp));
  }
  if (adv.length) {
    const grid = h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', width: '100%', marginTop: '6px' } }, ...adv.map(mkField));
    form.appendChild(h('details', { style: { width: '100%', marginTop: '2px' } },
      h('summary', { style: { cursor: 'pointer', color: '#94a3b8', fontSize: '12px', userSelect: 'none' } }, 'Advanced - ' + adv.length + ' overrides'),
      grid));
  }
  panel.__form = form;
  return form;
}

function collectParams(form, m) {
  const out = {};
  for (const inp of (m.inputs || [])) {
    const f = form.querySelector('[name="' + inp.key + '"]');
    if (!f) { if (inp.default != null) out[inp.key] = inp.default; continue; }
    if (inp.type === 'checkbox') out[inp.key] = f.checked;
    else if (inp.type === 'number') out[inp.key] = parseFloat(f.value);
    else if (inp.type === 'file') out[inp.key] = f.__fileText || '';
    else if ((inp.type === 'text' || inp.type === 'textarea') && typeof inp.parse === 'function') out[inp.key] = inp.parse(f.value);
    else out[inp.key] = f.value;
  }
  return out;
}

// ---------- .gdb declarative runner ----------
function pathGet(obj, path) {
  if (path == null) return undefined;
  return String(path).split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
async function runDeclarative(m, params, host) {
  const req = {};
  const map = m.map || {};
  for (const k in map) { const v = map[k]; req[k] = (typeof v === 'string' && params[v] !== undefined) ? params[v] : v; }
  const result = await host.engine(m.engine, req);
  const out = m.outputs || {};
  const res = {};
  if (out.summary) res.summary = out.summary.map(row => [row[0], pathGet(result, row[1])]);
  if (out.trust) res.trust = result;
  if (out.charts) res.charts = out.charts.map(c => {
    const spec = Object.assign({}, c);
    if (typeof c.x === 'string') spec.x = pathGet(result, c.x);
    if (Array.isArray(c.series)) spec.series = c.series.map(s => Object.assign({}, s, { y: typeof s.y === 'string' ? pathGet(result, s.y) : s.y }));
    if (typeof c.z === 'string') spec.z = pathGet(result, c.z);
    // default x to 1..N when not supplied (common for *_history arrays)
    if (spec.x == null && spec.series && spec.series[0] && Array.isArray(spec.series[0].y)) spec.x = spec.series[0].y.map((_, i) => i + 1);
    return spec;
  });
  if (out.viewport3d) {
    const v = out.viewport3d; res.viewport3d = {
      points: pathGet(result, v.points), values: v.values ? pathGet(result, v.values) : undefined,
      lines: v.lines ? pathGet(result, v.lines) : undefined, title: v.title || m.name,
    };
  }
  return res;
}

// ---------- result rendering ----------
function renderResult(out, res, host, m) {
  out.innerHTML = '';
  out.style.opacity = '1'; delete out.dataset.stale; // fresh result - clear any stale marking
  if (!res) return;
  if (res.status) out.appendChild(h('div', { style: { color: 'var(--text-dim,#475569)', fontSize: '12px', margin: '0 0 8px', lineHeight: '1.5' } }, res.status));
  if (res.summary && res.summary.length) {
    const tbl = h('table', { class: 'plasma-table', style: { margin: '8px 0' } },
      h('tbody', {}, ...res.summary.map(r => h('tr', {}, h('td', { style: { fontWeight: '600' } }, String(r[0])), h('td', {}, fmt(r[1]))))));
    out.appendChild(tbl);
  }
  // trust is rendered automatically in the persistent trust region (see buildPanelBody)
  // charts flow in a wrap row: a chart may set `width` (e.g. '50%', '33%') so several sit
  // per row; the gap is subtracted from a % basis, and flex-grow fills the remainder.
  if (res.charts && res.charts.length) {
    const wrap = h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '10px 0' } });
    out.appendChild(wrap);
    res.charts.forEach(spec => {
      const w = spec && spec.width ? String(spec.width) : '100%';
      const basis = /%\s*$/.test(w) ? 'calc(' + w + ' - 12px)' : w;
      const cd = h('div', { style: { flexGrow: '1', flexShrink: '1', flexBasis: basis, minWidth: '240px', height: (spec && spec.height) ? String(spec.height) : '320px', boxSizing: 'border-box' } });
      wrap.appendChild(cd); try { host.chart(cd, spec); } catch (e) {}
    });
  }
  if (res.viewport3d && res.viewport3d.points) {
    // Run just runs: set the payload so the top-bar + in-panel 3D buttons are armed,
    // but do NOT pop the 3D overlay automatically - opening is on the button click.
    try { window.__gdbs3dPayload = res.viewport3d; } catch (_) {}
    out.appendChild(h('button', { class: 'btn btn-sm', style: { marginTop: '6px' }, onclick: () => host.viewport3d(res.viewport3d) }, '↗ View in 3D'));
  }
  if (res.html) { const hd = h('div', { style: { marginTop: '8px' } }); hd.innerHTML = res.html; out.appendChild(hd); }
  // Worklog/Gate SEAM: a Pro/HPC plugin may return a routable Worklog task and/or a client-facing
  // Gate deliverable. We render them as structured, EXPORTABLE artifacts (never a silent server
  // write - a real case needs a workflow the user chooses, and Worklog is a separate surface). The
  // artifact is schema-tagged JSON that Worklog's bundle-import and the Gate portal can ingest.
  if (res.worklog) out.appendChild(worklogArtifactCard(res.worklog, m));
  if (res.gate) out.appendChild(gateArtifactCard(res.gate, m));
}
function fmt(v) {
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toPrecision(6);
  if (v == null) return '-'; return String(v);
}

// ---------- Worklog / Gate artifact cards (the plugin->case/portal seam) ----------
// A download anchor built with a UTF-8 data URI - works with no host JS, no CSP issue.
function _artifactDownload(filename, mime, text, label, accent) {
  const href = 'data:' + mime + ';charset=utf-8,' + encodeURIComponent(text);
  return h('a', { href, download: filename, style: { display: 'inline-block', margin: '0 8px 0 0', padding: '6px 12px', background: accent, color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: '600', textDecoration: 'none' } }, label);
}
// A Worklog-routable task artifact. task = { title, stage?, priority?, dueDate?, sla?, fields:[{label,value,sealed?}], summary? }.
function worklogArtifactCard(task, m) {
  const accent = m.accent || '#7c3aed';
  const meta = [];
  if (task.stage) meta.push('Stage: ' + task.stage);
  if (task.priority) meta.push('Priority: ' + task.priority);
  if (task.dueDate) meta.push('Due: ' + task.dueDate);
  if (task.sla) meta.push('SLA: ' + task.sla);
  const rows = (task.fields || []).map(f => h('tr', {},
    h('td', { style: { fontWeight: '600', padding: '2px 10px 2px 0', color: '#475569', whiteSpace: 'nowrap' } }, String(f.label) + (f.sealed ? ' \u{1F512}' : '')),
    h('td', { style: { padding: '2px 0', color: '#334155' } }, String(f.value))));
  const json = JSON.stringify(Object.assign({ schema: 'gdbs.worklog.task/v1', plugin: m.tier + '/' + m.id }, task), null, 2);
  return h('div', { style: { marginTop: '12px', border: '1px solid ' + accent + '55', borderRadius: '8px', padding: '10px 12px', background: accent + '0c' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' } },
      h('span', { style: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', color: accent, textTransform: 'uppercase' } }, 'Worklog task'),
      h('b', { style: { fontSize: '13px', color: '#334155' } }, task.title || 'Task')),
    meta.length ? h('div', { style: { fontSize: '11.5px', color: '#64748b', marginBottom: '6px' } }, meta.join('  ·  ')) : null,
    (task.fields && task.fields.length) ? h('table', { style: { fontSize: '11.5px', margin: '2px 0 8px', borderCollapse: 'collapse' } }, h('tbody', {}, ...rows)) : null,
    h('div', {},
      _artifactDownload(m.id + '-worklog-task.json', 'application/json', json, 'Export task (JSON)', accent),
      h('a', { href: '/worklog.html', target: '_blank', rel: 'noopener', style: { fontSize: '12px', color: accent, textDecoration: 'none' } }, 'Open Worklog ↗')),
    h('p', { style: { fontSize: '10.5px', color: '#94a3b8', marginTop: '6px' } }, 'Routable task artifact. Import into a Worklog case or track it there. Fields marked with a lock are sealed (stay on your device).'));
}
// A Gate-viewable client deliverable. gate = { title, deliverable?, viewable?, files?, summary? }.
function gateArtifactCard(gate, m) {
  const accent = '#0ea5e9';
  const json = JSON.stringify(Object.assign({ schema: 'gdbs.gate.deliverable/v1', plugin: m.tier + '/' + m.id }, gate), null, 2);
  return h('div', { style: { marginTop: '10px', border: '1px solid ' + accent + '55', borderRadius: '8px', padding: '10px 12px', background: accent + '0c' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' } },
      h('span', { style: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.04em', color: accent, textTransform: 'uppercase' } }, 'Client deliverable (Gate)'),
      h('b', { style: { fontSize: '13px', color: '#334155' } }, gate.title || 'Deliverable')),
    gate.summary ? h('div', { style: { fontSize: '11.5px', color: '#475569', marginBottom: '6px' } }, String(gate.summary)) : null,
    h('div', {},
      _artifactDownload(m.id + '-gate-deliverable.json', 'application/json', json, 'Export for Gate', accent),
      h('a', { href: '/pages/modules/module-gate.html', target: '_blank', rel: 'noopener', style: { fontSize: '12px', color: accent, textDecoration: 'none' } }, 'About Gate ↗')),
    h('p', { style: { fontSize: '10.5px', color: '#94a3b8', marginTop: '6px' } }, 'Client-viewable artifact for the Gate portal. Nothing is shared with a client until you publish it there.'));
}
// HPC automation: a plugin may export automation:{ onStart, onSuccess } so Worklog Automation
// (HPC) can fire it on a stage transition. Register it in a global map keyed by tier/id; the
// worklog-automation page reads this to bind stages to plugin runs. Registration is inert until
// a workflow references it - no behavior change for a plugin that ships no automation block.
function registerPluginAutomation(m) {
  try {
    if (!m || !m.automation || typeof m.automation !== 'object') return;
    if (typeof window === 'undefined') return;
    window.gdbsAutomation = window.gdbsAutomation || {};
    window.gdbsAutomation[m.tier + '/' + m.id] = { id: m.id, tier: m.tier, name: m.name, hooks: m.automation };
  } catch (e) { /* non-fatal */ }
}

// ---------- dev-log / bug-log entry + buttons (reuses engine-devlog.js show) ----------
function pluginEntry(m) {
  return {
    // key = the marketplace id (submission id for uploads, gdbs:tier/file for built-ins)
    // so the dev-log/bug-log edit permission (admin or submitter) resolves correctly.
    key: m._id || ('plugin-' + m.id), title: m.name || m.id, physics: m.blurb || '', refs: m.references || [],
    devlog: m.devlog || [{ stage: 'Plugin loaded', detail: 'Loaded dynamically from /plugins/' + m.tier + '/.', validated: 'manifest validated by the loader' }],
    buglog: m.buglog || [{ title: 'No defects surfaced in validation', symptom: '-', caughtBy: 'the published references declared by the plugin', fix: '-', status: 'clean' }],
    trust: m.trust || { tracked: false, mechanism: 'trust is bound automatically: the GeoNum verdict of the run result is shown live, or [UNTRACKED] when the plugin routes no drift', tiers: 'EXACT/PRECISE/VALID/DEGRADED/UNRELIABLE/[UNTRACKED]' },
  };
}
function logBtn(label, icon, color, onClick) {
  return h('button', { type: 'button', style: { flex: '0 0 auto', fontSize: '12px', fontWeight: '600', padding: '3px 10px', marginLeft: '6px', border: '1px solid ' + color, borderRadius: '6px', background: color + '1f', color: color, cursor: 'pointer', whiteSpace: 'nowrap' }, onclick: onClick },
    h('i', { class: 'fas fa-' + icon }), ' ' + label);
}

// ---------- panel body (rendered into the catalog detail region) ----------
function buildPanelBody(container, m) {
  container.innerHTML = '';
  registerPluginAutomation(m); // HPC automation hooks become bindable once the panel exists
  const accent = m.accent || '#60a5fa';
  // Page-title tint by tier (std grey, pro orange, hpc green) - the tier scheme lives on the PAGE
  // heading, not the catalog cards (those stay black). Matches the tier badge beside it.
  const tierCol = TIER_COLOR[m.tier] || accent;
  const entry = pluginEntry(m);
  const head = h('h3', { style: { margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
    h('i', { class: 'fas ' + (m.icon || 'fa-puzzle-piece'), style: { color: accent } }),
    h('span', { style: { color: tierCol } }, m.name || m.id),
    h('span', { style: { fontSize: '10px', background: accent + '22', color: accent, border: '1px solid ' + accent + '66', borderRadius: '9999px', padding: '1px 8px' } }, m.tier + ' plugin'),
    logBtn('dev log', 'list-check', '#3b82f6', () => { if (window.gdbsEngineLog && window.gdbsEngineLog.show) window.gdbsEngineLog.show('dev', entry); }),
    logBtn('bugs', 'bug', '#ef4444', () => { if (window.gdbsEngineLog && window.gdbsEngineLog.show) window.gdbsEngineLog.show('bugs', entry); }));
  container.appendChild(head);
  // author + origin + topic tags
  const meta = h('div', { style: { fontSize: '11px', color: '#94a3b8', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } });
  meta.appendChild(h('span', {}, h('i', { class: 'fas fa-user-circle', style: { marginRight: '4px', opacity: '0.7' } }), 'by ', h('b', { style: { color: 'var(--text,#e2e8f0)' } }, m._author || '-')));
  try { meta.appendChild(market.originBadge(m._origin)); } catch (e) {}
  (m._tags || []).forEach(t => meta.appendChild(h('span', { style: { fontSize: '10px', padding: '1px 8px', borderRadius: '9999px', background: 'rgba(96,165,250,0.12)', color: '#60a5fa' } }, t)));
  container.appendChild(meta);
  if (m._sandboxed) {
    container.appendChild(h('div', { style: { fontSize: '11px', color: '#34d399', border: '1px solid #34d39955', background: 'rgba(52,211,153,0.08)', borderRadius: '6px', padding: '5px 9px', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '6px' }, title: 'Third-party code runs in an isolated module worker with network/DOM primitives removed, a frozen GDBS capability (compute + log only), and a watchdog timeout.' },
      h('i', { class: 'fas fa-shield-halved' }), 'Third-party plugin - sandboxed: isolated worker, no network, frozen GDBS capability, watchdog.'));
  }
  if (m.blurb) container.appendChild(h('p', { style: { color: '#94a3b8', fontSize: '12px', margin: '2px 0 6px' } }, m.blurb));
  if (m.references && m.references.length) {
    container.appendChild(h('details', { style: { fontSize: '11px', color: '#64748b', margin: '0 0 6px' } },
      h('summary', { style: { cursor: 'pointer' } }, 'References (' + m.references.length + ')'),
      h('ul', { style: { margin: '4px 0 0 16px' } }, ...m.references.map(r => h('li', {}, r)))));
  }
  const host = createHost(m, container);
  // GeoNum trust - bound automatically as soon as the plugin loads ([UNTRACKED]
  // until the first run), then updated live from the raw run result.
  const trustRegion = h('div', { class: 'plugin-trust', style: { margin: '10px 0 0' } });
  trustRegion.innerHTML = host.trustHTML({});
  // NOTE: appended AFTER the output (below), so the GeoNum verdict reads as a
  // result of the run rather than sitting above the inputs.

  if (typeof m.render === 'function') { try { m.render(container, host); } catch (e) { container.appendChild(h('div', { style: { color: '#ef4444' } }, 'plugin render error: ' + e.message)); } return; }

  if (!entitled(m)) {
    const lock = h('div', { style: { padding: '10px 12px', border: '1px solid #fbbf24', borderRadius: '6px', background: 'rgba(251,191,36,0.08)', color: '#fbbf24', fontSize: '13px', margin: '8px 0' } },
      h('i', { class: 'fas fa-lock' }), ' This plugin requires ' + (m.requiredModule || m.tier.toUpperCase()) + ' access.');
    // Self-serve unlock: when the requiredModule is a known standalone add-on SKU, offer a
    // direct one-click checkout (mirrors alacarte.js -> window.startPlanCheckout) instead of
    // only pointing at the pricing modal. The webhook grants the module on payment.
    const ADDON_SKU = { medical_research: { sku: 'sku_ala_medical_research', price: '$9.99/mo' } };
    const buy = m.requiredModule && ADDON_SKU[m.requiredModule];
    if (buy) {
      lock.appendChild(h('button', { class: 'btn btn-sm btn-primary', style: { marginLeft: '10px' },
        onclick: (e) => { try { window.startPlanCheckout([buy.sku], e.currentTarget, { immediate: true }); } catch (err) {} } },
        h('i', { class: 'fas fa-bolt' }), ' Unlock - ' + buy.price));
    }
    container.appendChild(lock);
  }
  const form = buildForm(container, m);
  const out = h('div', { class: 'plugin-output' });
  const runBtn = h('button', { class: 'btn btn-primary', type: 'submit' }, h('i', { class: 'fas fa-play' }), ' Run');
  form.appendChild(runBtn);
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    if (!entitled(m)) return;
    runBtn.disabled = true; out.innerHTML = '<span style="color:#94a3b8;font-size:12px;">running...</span>';
    showPluginLoading(m.name || m.id);
    await _pluginNextFrame(); // paint the modal before the synchronous wasm call blocks the main thread
    try {
      const params = collectParams(form, m);
      // RUNTIME authorization: fetch the entitlement-bound run key (no valid token/share -> no key
      // -> refuse the run and show the read-only kiosk view). Attached to the host so a plugin can
      // fold it into its result; a scraped copy run outside the app cannot obtain it. Non-gated
      // (.gdb/external) plugins get null and run normally.
      try { host.runtimeKey = await fetchRuntimeKey(m); }
      catch (keyErr) { out.innerHTML = ''; out.appendChild(kioskLockedPanel(m)); return; }
      const res = m._declarative ? await runDeclarative(m, params, host) : await m.run(params, host);
      // Auto-bind GeoNum trust: prefer an explicit res.trust, else the raw result of
      // the last engine() call, else the structured result (-> [UNTRACKED] for pure JS).
      const trustSrc = (res && res.trust != null) ? res.trust : (host._lastResult != null ? host._lastResult : (res || {}));
      trustRegion.innerHTML = host.trustHTML(trustSrc); trustRegion.style.opacity = '1';
      try { if (window.gdbsTrust && window.gdbsTrust.update) window.gdbsTrust.update('plugin', m.id, JSON.stringify(host._lastResult || res || {})); } catch (e) {}
      renderResult(out, res, host, m);
      host.log('run', { plugin: m.id });
      // GTI Verify Badge: after a successful STD run, offer a one-click "Copy verify
      // badge" that mints a read-only share token + emits self-reproducing embed
      // snippets. Only for std plugins, and never for a kiosk visitor (who is viewing a
      // shared re-run and must not re-mint). Fully guarded - a failure never breaks a run.
      try { if (m.tier === 'std' && !window.__kioskTarget) mountVerifyBadge(out, { m, form, params, res, host }); } catch (e) {}
    } catch (e) {
      out.innerHTML = '<div style="color:#ef4444;font-size:12px;">Error: ' + (e && e.message ? e.message : e) + '</div>';
      console.warn('[plugin ' + m.id + '] run error', e);
    } finally { runBtn.disabled = false; hidePluginLoading(); }
  });
  container.appendChild(form);
  container.appendChild(out);
  container.appendChild(trustRegion);
  // Caught in review: changing an input (e.g. the standards-model dropdown) without
  // re-running left a prior result on screen that read as current. Mark the output +
  // trust stale on any input change so a stale render can't be mistaken for the run.
  const markStale = () => {
    if (!out.firstChild || out.dataset.stale === '1') return;
    out.dataset.stale = '1'; out.style.opacity = '0.5'; trustRegion.style.opacity = '0.5';
    out.insertBefore(h('div', { style: { color: '#b45309', fontSize: '12px', fontWeight: '600', margin: '0 0 8px' } }, '⚠ Inputs changed - press Run to refresh this result.'), out.firstChild);
  };
  form.addEventListener('input', markStale);
  form.addEventListener('change', markStale);
  // ratings / reviews for this plugin (per-user, one rating each)
  const ratings = h('div', { class: 'plugin-ratings', style: { marginTop: '14px', borderTop: '1px solid var(--border,#334155)', paddingTop: '10px' } });
  container.appendChild(ratings);
  try { market.renderRatings(ratings, m._id || ('gdbs:' + m.tier + '/' + m.id)); } catch (e) {}
}

// ---------- loading modal for plugin runs ----------
// Reuses the shared #hpc-loading-modal (the one BSSN/MD/hpc-lab drive). Plugin
// compute now runs in a Web Worker (engine-runner.worker via plugin-host), so
// the main thread stays responsive: the spinner animates, the elapsed timer
// ticks live, and the browser never raises the "unresponsive script" dialog.
function _pluginNextFrame() {
  return new Promise((res) => requestAnimationFrame(() => requestAnimationFrame(res)));
}
let _pluginLoadTimer = null;
function showPluginLoading(name) {
  const modal = document.getElementById('hpc-loading-modal');
  const titleEl = document.getElementById('hpc-loading-title');
  const textEl = document.getElementById('hpc-loading-text');
  const progEl = document.getElementById('hpc-loading-progress');
  const cancelBtn = document.getElementById('hpc-loading-cancel');
  if (titleEl) titleEl.textContent = 'Running ' + (name || 'plugin');
  if (textEl) textEl.textContent = 'Computing on the in-browser engine';
  // Compute runs in a worker now, so Cancel is real: terminate it.
  if (cancelBtn) { cancelBtn.style.display = ''; cancelBtn.textContent = 'Cancel'; cancelBtn.onclick = () => { try { cancelEngine(); } catch (e) {} }; }
  const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : null;
  if (progEl) progEl.textContent = '0.0 s';
  if (_pluginLoadTimer) clearInterval(_pluginLoadTimer);
  if (t0 !== null) {
    _pluginLoadTimer = setInterval(() => {
      if (progEl) progEl.textContent = ((performance.now() - t0) / 1000).toFixed(1) + ' s';
    }, 100);
  }
  if (modal) modal.classList.remove('hidden');
}
function hidePluginLoading() {
  const modal = document.getElementById('hpc-loading-modal');
  if (modal) modal.classList.add('hidden');
  if (_pluginLoadTimer) { clearInterval(_pluginLoadTimer); _pluginLoadTimer = null; }
  const cancelBtn = document.getElementById('hpc-loading-cancel');
  if (cancelBtn) { cancelBtn.style.display = ''; cancelBtn.onclick = null; }
}

// ---------- catalog page (ONE place for all plugins; cards float left) ----------
const TIER_ORDER = { std: 0, pro: 1, hpc: 2 };

// Show the admin review-queue button only if the caller is an admin (the endpoint
// 403s otherwise). Probed once when the catalog is built.
async function probeAdmin(toolbar) {
  // Review queue is admin-only; checking the role first avoids a guaranteed 403
  // probe (and its console error) for every non-admin visitor.
  if (getUser()?.role !== 'admin') return;
  if (!market.isRegistered() || toolbar.querySelector('[data-admin-review]')) return;
  try {
    const r = await market.authFetch('/api/plugins/pending');
    if (!r.ok) return;
    const j = await r.json().catch(() => ({ plugins: [] }));
    const n = (j.plugins || []).length;
    toolbar.appendChild(h('button', { class: 'btn btn-sm', 'data-admin-review': '1', style: { borderColor: '#ef4444', color: '#ef4444' }, onclick: () => market.openReviewQueue() },
      h('i', { class: 'fas fa-clipboard-check' }), ' Review queue' + (n ? ' (' + n + ')' : '')));
    toolbar.appendChild(h('button', { class: 'btn btn-sm', style: { borderColor: '#fbbf24', color: '#fbbf24' }, onclick: () => market.openVssQueue((id) => {
      const card = Array.from(document.querySelectorAll('#plugin-catalog .plugin-card')).find(c => c.__m && c.__m._id === id);
      if (card && card.__m) { card.__m._vssApproved = true; card.remove(); addCard(card.__m); }
    }) }, h('i', { class: 'fas fa-circle-check' }), ' VSS review'));
    catalogIsAdmin = true; // admins manage every plugin (edit/delete on card hover)
    revealManage();
  } catch (e) {}
}

function ensureCatalog() {
  let view = document.getElementById('view-plugins');
  if (view) return view;
  const sibling = document.querySelector('.view');
  if (!sibling || !sibling.parentElement) return null;
  view = h('div', { id: 'view-plugins', class: 'view' });
  const panel = h('div', { class: 'panel' });
  const body = h('div', { class: 'panel-content', style: { padding: '16px' } });
  body.appendChild(h('h2', { style: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 4px' } },
    h('i', { class: 'fas fa-puzzle-piece', style: { color: '#a78bfa' } }), 'Plugins', h('span', { id: 'plugin-cat-tier', style: { color: '#94a3b8', fontWeight: '400' } }, '')));
  body.appendChild(h('p', { style: { color: '#94a3b8', fontSize: '13px', margin: '0 0 10px' } },
    'First-party (VaultSync Solutions) and VSS Approved plugins sort to the top; the rest are ranked by community rating. Click one to open it.'));
  const toolbar = h('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', margin: '0 0 12px' } },
    h('button', { class: 'btn btn-primary btn-sm', onclick: () => market.openUploadModal() }, h('i', { class: 'fas fa-upload' }), ' Upload a plugin'),
    h('button', { class: 'btn btn-sm', onclick: () => market.openDocsModal() }, h('i', { class: 'fas fa-book' }), ' How to create a plugin'),
    h('button', { class: 'btn btn-sm', onclick: () => import('./plugin-designer.js').then(m => m.openDesigner()).catch(() => {}) }, h('i', { class: 'fas fa-microchip' }), ' Engine Designer'));
  body.appendChild(toolbar);
  // Free-text filter across name / tags / description (works with the tier + tag-chip filters).
  const search = h('input', { type: 'search', placeholder: 'Filter plugins by name, tag, or description...', class: 'form-control', style: { width: '100%', maxWidth: '440px', margin: '0 0 12px' } });
  search.addEventListener('input', () => { searchQuery = search.value.trim().toLowerCase(); applyFilter(); });
  body.appendChild(search);
  // Standards & security note (visible, collapsible) - the sandbox + trust + validation model.
  body.appendChild(h('details', { style: { margin: '0 0 12px', border: '1px solid #34d39955', background: 'rgba(52,211,153,0.06)', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#94a3b8' } },
    h('summary', { style: { cursor: 'pointer', color: '#34d399', fontWeight: '600' } }, h('i', { class: 'fas fa-shield-halved', style: { marginRight: '6px' } }), 'Standards & security'),
    h('div', { style: { marginTop: '8px', lineHeight: '1.6' } },
      h('p', { style: { margin: '0 0 6px' } }, h('b', { style: { color: 'var(--text,#e2e8f0)' } }, 'Execution sandbox. '),
        'Community plugins execute only in an isolated module Web Worker under a strict, worker-scoped Content-Security-Policy that blocks external module loading and all network egress at the platform level. Plugin code has no DOM, network, or GPU access and receives only a frozen, host-mediated capability surface, with a watchdog enforcing a compute budget. First-party plugins are code-reviewed and run in the main realm.'),
      h('p', { style: { margin: '0 0 6px' } }, h('b', { style: { color: 'var(--text,#e2e8f0)' } }, 'Review and trust. '),
        'Every community upload is automatically screened at submission - a security screen plus a sandboxed dry-run in the same isolation users get. A clean plugin goes live immediately at UNVERIFIED trust; a policy violation is auto-rejected and flagged to an administrator. Each plugin carries a trust badge: VaultSync Solutions plugins are trusted by default; community plugins are auto-screened (not blessed) and earn their level from community reviews. An administrator can override a decision or ban an account.'),
      h('p', { style: { margin: '0' } }, h('b', { style: { color: 'var(--text,#e2e8f0)' } }, 'Validation. '),
        'Plugins validate against published, peer-reviewed references and surface a GeoNum trust verdict (or an honest [UNTRACKED]) on every result - never a fabricated pass.'))));
  const mine = h('div', { id: 'plugin-mine', style: { margin: '0 0 10px' } });
  body.appendChild(mine);
  body.appendChild(h('div', { id: 'plugin-tagfilter', style: { margin: '0 0 10px' } })); // topic-tag filter chips
  body.appendChild(h('div', { id: 'plugin-catalog', style: { display: 'flow-root' } })); // clearfix row of floated cards
  setTimeout(() => { try { market.renderMyPlugins(mine); } catch (e) {} probeAdmin(toolbar); }, 250);
  // Opening a plugin switches to its OWN view (#view-plugin-open), not an inline region -
  // each plugin is its own solution, and only the opened one is mounted/run (scales to many).
  panel.appendChild(body); view.appendChild(panel); sibling.parentElement.appendChild(view);
  addNavLinks();
  return view;
}

// A plugin reads "New" for 30 days after its manifest `added` date (carried into the
// catalog by gen-plugin-index). Missing / unparseable / future date -> not new: we never
// flag something as new without a real, in-range add-date.
function isNewPlugin(m) {
  const d = m && (m._added || m.added);
  if (!d) return false;
  const t = Date.parse(d);
  if (!isFinite(t)) return false;
  const age = Date.now() - t;
  return age >= 0 && age <= 30 * 24 * 60 * 60 * 1000;
}
function addCard(m) {
  if (!ensureCatalog()) return;
  const catalog = document.getElementById('plugin-catalog');
  const accent = m.accent || '#60a5fa';
  const blurb = m.blurb || '';
  const ord = (TIER_ORDER[m.tier] == null) ? 99 : TIER_ORDER[m.tier];
  const tags = m._tags || [];
  const tierCol = TIER_COLOR[m.tier] || accent;
  // Uniform cards: fixed WIDTH and HEIGHT so they read as a clean grid. Position relative so
  // the trust badge can overlay the top-right corner. Name is clamped (right-padded to clear
  // the badge), blurb clamped to 3 lines, a spacer pins tags+footer to a common baseline.
  const card = h('button', { type: 'button', class: 'plugin-card', 'data-pid': m.tier + '/' + m.id, 'data-ord': String(ord), 'data-tier': m.tier, 'data-tags': tags.join('|'),
    'data-search': ((m.name || '') + ' ' + (m.blurb || '') + ' ' + tags.join(' ')).toLowerCase(),
    'data-vss': isVssEndorsed(m) ? '0' : '1', 'data-rating': String(m._rating || 0),
    style: { position: 'relative', float: 'left', textAlign: 'left', cursor: 'pointer', width: '300px', height: '200px', boxSizing: 'border-box', margin: '0 12px 12px 0', border: '1px solid var(--border,#334155)', borderRadius: '8px', background: 'var(--bg-card,rgba(255,255,255,0.02))', padding: '12px', display: 'flex', flexDirection: 'column', gap: '5px', overflow: 'hidden' } });
  // trust badge overlay (top-right), coloured by trust level
  card.appendChild(trustBadgeEl(m));
  const fresh = isNewPlugin(m);
  // header: icon + name (clamped). Title is flush-left and black for EVERY card - the "New" marker is
  // rendered INLINE after the name (not an absolute top-left badge) so no card's title gets indented.
  // Right pad only clears the top-right trust badge, which every card has (so it stays uniform).
  card.appendChild(h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flex: '0 0 auto', paddingRight: '78px' } },
    h('i', { class: 'fas ' + (m.icon || 'fa-puzzle-piece'), style: { color: accent, flex: '0 0 auto' } }),
    h('span', { style: { fontWeight: '600', color: '#0f172a', textAlign: 'left', flex: '1', minWidth: '0', fontSize: '13px', lineHeight: '1.25', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxOrient: 'vertical', overflow: 'hidden' }, title: m.name || m.id }, m.name || m.id),
    fresh ? h('span', { title: 'New - added within the last 30 days', style: { flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.02em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: '9999px', color: '#16a34a', background: 'rgba(22,163,74,0.14)', border: '1px solid #16a34a88', whiteSpace: 'nowrap' } }, h('span', { style: { color: '#f5b400', fontSize: '10px', lineHeight: '1' } }, '★'), 'New') : null));
  // blurb (clamped to 3 lines)
  card.appendChild(h('div', { style: { fontSize: '11px', color: '#94a3b8', lineHeight: '1.4', flex: '0 0 auto', display: '-webkit-box', WebkitLineClamp: '3', WebkitBoxOrient: 'vertical', overflow: 'hidden' } }, blurb));
  // spacer -> pins tags + footer to the bottom for a consistent baseline
  card.appendChild(h('div', { style: { flex: '1 1 auto' } }));
  // topic-tag chips (single clipped line); clicking one filters the catalog (not open)
  if (tags.length) {
    const tr = h('div', { style: { display: 'flex', gap: '4px', flexWrap: 'nowrap', overflow: 'hidden', maxHeight: '17px', flex: '0 0 auto' } });
    tags.slice(0, 4).forEach(t => {
      const chip = h('span', { style: { fontSize: '9px', padding: '1px 7px', borderRadius: '9999px', background: 'rgba(96,165,250,0.12)', color: '#60a5fa', cursor: 'pointer', whiteSpace: 'nowrap' } }, t);
      chip.addEventListener('click', e => { e.stopPropagation(); setActiveTag(activeTag === t ? null : t); });
      tr.appendChild(chip);
    });
    card.appendChild(tr);
  }
  // footer: [VSS Approved] + origin + tier (tier-coloured) + rating + author
  const footer = h('div', { style: { fontSize: '10px', color: '#64748b', display: 'flex', gap: '7px', alignItems: 'center', flex: '0 0 auto' } });
  if (m._origin === 'user' && m._vssApproved) footer.appendChild(h('span', { title: 'VSS Approved - reviewed and endorsed by VaultSync Solutions', style: { flex: '0 0 auto', fontSize: '9px', fontWeight: '700', color: '#fbbf24', border: '1px solid #fbbf2288', background: 'rgba(251,191,36,0.14)', borderRadius: '9999px', padding: '0 6px', whiteSpace: 'nowrap' } }, h('i', { class: 'fas fa-circle-check', style: { marginRight: '3px' } }), 'VSS'));
  footer.appendChild(market.originBadge(m._origin));
  footer.appendChild(h('span', { style: { flex: '0 0 auto', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', color: tierCol, border: '1px solid ' + tierCol + '88', background: tierCol + '1a', borderRadius: '9999px', padding: '0 6px' } }, m.tier));
  footer.appendChild(h('span', { style: { color: '#f59e0b', flex: '0 0 auto' } }, m._ratingCount ? ('★ ' + Number(m._rating).toFixed(1) + ' (' + m._ratingCount + ')') : 'unrated'));
  footer.appendChild(h('span', { style: { marginLeft: 'auto', minWidth: '0', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }, title: 'by ' + (m._author || '') }, 'by ' + (m._author || '-')));
  card.appendChild(footer);
  card.addEventListener('click', () => openPlugin(m, card));
  card.__m = m;
  addCardManage(card, m); // admin-only manage on hover (no-op until the admin probe confirms)
  // VSS-endorsed (first-party + VSS Approved) sort to the top, then by rating
  catalog.appendChild(card);
  sortCatalog();
  renderTagFilter(); applyFilter();
}

// ---- admin manage controls (edit / delete) revealed on card hover ----
function builtinFile(m) {
  if (m._id && m._id.indexOf('/') !== -1) return m._id.split('/').pop();
  if (m._sourceUrl) return m._sourceUrl.split('/').pop();
  return (m.id || 'plugin') + '.plugin.es';
}
async function managePlugin(action, m) {
  const builtin = (m._origin === 'gdbs');
  if (action === 'edit') {
    if (builtin) market.openBuiltinEditor(m.tier, builtinFile(m), m.name, () => {});
    else market.openUploadModal(m._id);
    return;
  }
  if (action === 'vss') { // grant/revoke the "VSS Approved" blessing (community plugins only)
    if (builtin) return;
    const grant = !m._vssApproved;
    let r;
    try { r = await market.authFetch('/api/plugins/' + encodeURIComponent(m._id) + '/' + (grant ? 'vss-approve' : 'vss-unapprove'), { method: 'POST' }); } catch (e) { r = null; }
    if (r && r.ok) {
      m._vssApproved = grant;
      const card = document.querySelector('#plugin-catalog .plugin-card[data-pid="' + m.tier + '/' + m.id + '"]');
      if (card) card.remove();
      addCard(m); // rebuild with the VSS Approved badge + re-sort to the top
    } else { window.alert('VSS toggle failed' + (r ? ' (' + r.status + ')' : '')); }
    return;
  }
  if (!window.confirm('Delete "' + (m.name || m.id) + '"? This removes it' + (builtin ? ' (the live built-in file)' : ' and its reviews') + '.')) return;
  let r;
  try {
    if (builtin) r = await market.authFetch('/api/plugins/builtin/delete', { method: 'POST', body: JSON.stringify({ tier: m.tier, file: builtinFile(m) }) });
    else r = await market.authFetch('/api/plugins/' + encodeURIComponent(m._id) + '/delete', { method: 'POST' });
  } catch (e) { r = null; }
  if (r && r.ok) {
    const card = document.querySelector('#plugin-catalog .plugin-card[data-pid="' + m.tier + '/' + m.id + '"]');
    if (card) card.remove();
    renderTagFilter();
  } else { window.alert('Delete failed' + (r ? ' (' + r.status + ')' : '')); }
}
function addCardManage(card, m) {
  if (!catalogIsAdmin || card.querySelector('.plugin-manage')) return;
  const mk = (icon, color, title, fn) => {
    const b = h('span', { title: title, style: { cursor: 'pointer', color: color, background: 'var(--bg,#0b1020)', border: '1px solid ' + color + '88', borderRadius: '6px', padding: '2px 7px', fontSize: '11px', lineHeight: '1' } }, h('i', { class: 'fas ' + icon }));
    b.addEventListener('click', e => { e.stopPropagation(); fn(); });
    return b;
  };
  const btns = [mk('fa-pen', '#60a5fa', 'Edit', () => managePlugin('edit', m))];
  if (m._origin === 'user') btns.push(mk('fa-circle-check', m._vssApproved ? '#fbbf24' : '#94a3b8', m._vssApproved ? 'VSS Approved (click to revoke)' : 'Grant VSS Approved', () => managePlugin('vss', m)));
  btns.push(mk('fa-trash', '#ef4444', 'Delete', () => managePlugin('delete', m)));
  const grp = h('span', { class: 'plugin-manage', style: { position: 'absolute', top: '8px', left: '8px', display: 'none', gap: '5px', zIndex: '4' } }, ...btns);
  card.appendChild(grp);
  card.addEventListener('mouseenter', () => { grp.style.display = 'inline-flex'; });
  card.addEventListener('mouseleave', () => { grp.style.display = 'none'; });
}
function revealManage() {
  document.querySelectorAll('#plugin-catalog .plugin-card').forEach(card => { if (card.__m) addCardManage(card, card.__m); });
}

// ---- catalog filtering: scoped to the current tier + an optional topic tag ----
function setTier(tier) { currentTier = tier || null; activeTag = null; renderTagFilter.expanded = false; updateCatTitle(); renderTagFilter(); applyFilter(); }
function setActiveTag(t) { activeTag = t; renderTagFilter(); applyFilter(); }
function applyFilter() {
  document.querySelectorAll('#plugin-catalog .plugin-card').forEach(card => {
    const okTier = !currentTier || card.getAttribute('data-tier') === currentTier;
    const ct = (card.getAttribute('data-tags') || '').split('|').filter(Boolean);
    const okTag = !activeTag || ct.indexOf(activeTag) !== -1;
    const okSearch = !searchQuery || (card.getAttribute('data-search') || card.textContent || '').toLowerCase().includes(searchQuery);
    card.style.display = (okTier && okTag && okSearch) ? '' : 'none';
  });
}
// VSS-endorsed plugins (first-party + VSS Approved) float to the top; the rest rank by rating.
function sortCatalog() {
  const catalog = document.getElementById('plugin-catalog');
  if (!catalog) return;
  Array.from(catalog.children).sort((a, b) => {
    const va = a.getAttribute('data-vss') || '1', vb = b.getAttribute('data-vss') || '1';
    if (va !== vb) return va < vb ? -1 : 1;                              // endorsed (0) first
    const ra = parseFloat(a.getAttribute('data-rating') || '0'), rb = parseFloat(b.getAttribute('data-rating') || '0');
    if (rb !== ra) return rb - ra;                                       // then rating desc
    const oa = parseInt(a.getAttribute('data-ord') || '99', 10), ob = parseInt(b.getAttribute('data-ord') || '99', 10);
    return oa - ob;                                                      // then tier order
  }).forEach(c => catalog.appendChild(c));
}
function updateCatTitle() {
  const el = document.getElementById('plugin-cat-tier');
  if (el) el.textContent = currentTier ? (' - ' + ({ std: 'Standard', pro: 'Pro', hpc: 'HPC' }[currentTier] || currentTier)) : '';
}
function renderTagFilter() {
  const bar = document.getElementById('plugin-tagfilter');
  if (!bar) return;
  bar.innerHTML = '';
  // aggregate tags ONLY from cards in the current tier scope
  const counts = new Map();
  document.querySelectorAll('#plugin-catalog .plugin-card').forEach(card => {
    if (currentTier && card.getAttribute('data-tier') !== currentTier) return;
    (card.getAttribute('data-tags') || '').split('|').filter(Boolean).forEach(t => counts.set(t, (counts.get(t) || 0) + 1));
  });
  if (!counts.size) return;
  const chip = (label, active, onClick) => {
    const b = h('button', { type: 'button', style: { fontSize: '11px', padding: '2px 9px', borderRadius: '9999px', cursor: 'pointer', whiteSpace: 'nowrap',
      border: '1px solid ' + (active ? '#60a5fa' : 'var(--border,#334155)'), background: active ? 'rgba(96,165,250,0.18)' : 'transparent', color: active ? '#60a5fa' : 'var(--text-dim,#94a3b8)' } }, label);
    b.addEventListener('click', onClick); return b;
  };
  bar.appendChild(h('span', { style: { fontSize: '12px', color: '#888', marginRight: '6px' } }, 'Filter by tag:'));
  const row = h('span', { style: { display: 'inline-flex', gap: '6px', flexWrap: 'wrap', verticalAlign: 'middle' } });
  // Collapse the bar: by default show only the highest-frequency tags (the ones that actually group
  // multiple plugins); the long tail of single-plugin tags hides behind a "+N more" toggle. Applies
  // per tier (counts are tier-scoped above), so all three plugin systems get the same tidy bar.
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const CAP = 24;
  const expanded = !!renderTagFilter.expanded;
  let shown = expanded ? sorted : sorted.filter(([, c]) => c >= 2).slice(0, CAP);
  if (!expanded && shown.length < 8) shown = sorted.slice(0, Math.min(CAP, sorted.length)); // few multi-plugin tags: just take the top
  if (activeTag && !shown.some(([t]) => t === activeTag)) {
    const a = sorted.find(([t]) => t === activeTag); if (a) shown = [a, ...shown]; // keep an active tail-tag visible
  }
  shown.forEach(([t, c]) => row.appendChild(chip(t + ' (' + c + ')', activeTag === t, () => setActiveTag(activeTag === t ? null : t))));
  const hidden = sorted.length - shown.length;
  if (!expanded && hidden > 0) row.appendChild(chip('+' + hidden + ' more', false, () => { renderTagFilter.expanded = true; renderTagFilter(); }));
  else if (expanded && sorted.length > CAP) row.appendChild(chip('show fewer', false, () => { renderTagFilter.expanded = false; renderTagFilter(); }));
  if (activeTag) row.appendChild(chip('× clear', false, () => setActiveTag(null)));
  bar.appendChild(row);
}

// A dedicated, reusable view that holds whichever plugin is currently open. Created once;
// its body is rebuilt per open. This is the "open as its own solution" surface.
function ensurePluginView() {
  let view = document.getElementById('view-plugin-open');
  if (view) return view;
  const sibling = document.querySelector('.view');
  if (!sibling || !sibling.parentElement) return null;
  view = h('div', { id: 'view-plugin-open', class: 'view' });
  const panel = h('div', { class: 'panel' });
  const body = h('div', { id: 'plugin-open-body', class: 'panel-content', style: { padding: '16px' } });
  panel.appendChild(body); view.appendChild(panel); sibling.parentElement.appendChild(view);
  return view;
}

let _openSandbox = null; // the live sandbox worker for the currently-open plugin (if any)
function disposeOpenSandbox() { try { if (_openSandbox) _openSandbox.dispose(); } catch (e) {} _openSandbox = null; }

// Spin a fresh sandbox worker for a third-party plugin: fetch its source (same-origin),
// run it in the hardened worker, and pull back the form schema. m.run is rebound to a thin
// delegate so buildPanelBody runs the plugin THROUGH the worker (which has no network/DOM
// and a mediated, allow-listed engine capability + watchdog).
async function initSandbox(m) {
  const r = await fetch(m._sourceUrl, { cache: 'no-cache' });
  if (!r.ok) throw new Error('source ' + r.status);
  const code = await r.text();
  const engHost = createHost(m, null); // used only as the mediated engine + logger surface
  const sandbox = createSandbox(code, {
    engine: (n, req) => engHost.engine(n, req),
    allow: engHost.engines,
    onLog: (l, d) => { try { if (engHost.log) engHost.log(l, d); } catch (e) {} },
    timeoutMs: 20000,
  });
  const schema = await sandbox.init();
  if (schema) {
    for (const k of ['inputs', 'presets', 'references', 'version', 'devlog', 'buglog']) if (schema[k] != null) m[k] = schema[k];
    if (schema.author && !m._authorFromServer) m._author = m._author || schema.author;
  }
  m.run = (params) => sandbox.run(params); // buildPanelBody calls this; it delegates to the worker
  _openSandbox = sandbox;
}

async function openPlugin(m, card) {
  if (!ensurePluginView()) return;
  const body = document.getElementById('plugin-open-body');
  body.innerHTML = '';
  disposeOpenSandbox();
  body.appendChild(h('button', { class: 'btn btn-sm', style: { marginBottom: '12px' }, onclick: () => { disposeOpenSandbox(); try { window.gdbsShowView('plugins'); } catch (e) {} } },
    h('i', { class: 'fas fa-arrow-left' }), ' Back to plugins'));
  const host = h('div', {});
  body.appendChild(host);
  try { window.gdbsShowView('plugin-open'); } catch (e) {}
  try { body.scrollTop = 0; } catch (e) {}
  if (m._sandboxed) {
    host.appendChild(h('div', { style: { color: '#94a3b8', fontSize: '12px' } }, h('i', { class: 'fas fa-shield-halved', style: { marginRight: '6px' } }), 'Loading third-party plugin in the sandbox...'));
    try { await initSandbox(m); } catch (e) { host.innerHTML = '<div style="color:#ef4444;font-size:13px;">Sandbox failed: ' + ((e && e.message) || e) + '</div>'; return; }
  } else if (m._lazy && !m._codeLoaded) {
    // Lazy built-in: import its code now, ONLY for an entitled user. If not entitled,
    // render the locked upsell panel without ever fetching the plugin source.
    if (!entitled(m)) { buildPanelBody(host, m); return; }
    host.appendChild(h('div', { style: { color: '#94a3b8', fontSize: '12px' } }, h('i', { class: 'fas fa-circle-notch fa-spin', style: { marginRight: '6px' } }), 'Loading ' + (m.name || 'plugin') + '...'));
    let ok = false;
    try { ok = await ensurePluginCode(m); } catch (e) { ok = false; }
    // Enforced delivery: a denied/failed fetch is NOT a 404. Fall to the read-only kiosk view -
    // the solution stays visible, with a route to run it via an entitled account.
    if (!ok) { host.innerHTML = ''; host.appendChild(kioskLockedPanel(m)); return; }
  }
  buildPanelBody(host, m);                 // mount ONLY this plugin, on demand (buildPanelBody clears host)
  try { window.__openPlugin = { tier: m.tier, id: m.id }; } catch (e) {}  // for share-target detection
}

// Read-only "kiosk" presentation shown when plugin CODE could not be delivered (denied token,
// decrypt/encrypt error, network) - the enforced replacement for a 404/error. Keeps the solution
// visible and routes the visitor to run it via an entitled account, without exposing the code.
function kioskLockedPanel(m) {
  return h('div', { style: { padding: '22px', textAlign: 'center', color: '#475569', fontSize: '13px', lineHeight: '1.6' } },
    h('i', { class: 'fas fa-lock', style: { fontSize: '22px', color: '#7c3aed' } }),
    h('p', { style: { margin: '10px 0 4px', fontWeight: '700', color: '#1e293b', fontSize: '15px' } }, (m.name || 'This tool')),
    h('p', { style: { margin: '0 auto 12px', maxWidth: '30rem' } },
      'This solution is available read-only here. Sign in with an entitled account to run it with your own inputs.'),
    h('a', { href: '/', style: { color: '#fff', background: '#7c3aed', padding: '8px 16px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', display: 'inline-block' } }, 'Open in GDBS'));
}

// A "Plugins" nav link in each module section (std/pro/hpc), all opening the catalog.
function addNavLinks() {
  [['standard', 'std'], ['pro', 'pro'], ['ent', 'hpc']].forEach(pair => {
    const sec = document.querySelector('.nav-tier-section[data-tier="' + pair[0] + '"]');
    if (!sec || sec.querySelector('[data-view="plugins"]')) return;
    const btn = h('button', { class: 'nav-item ' + (pair[0] === 'standard' ? 'nav-standard' : 'nav-premium'), 'data-view': 'plugins', 'data-tier': pair[0] === 'standard' ? 'Standard' : 'Premium' },
      h('i', { class: 'fas fa-puzzle-piece' }), h('span', {}, 'Plugins'));
    // open the catalog scoped to THIS tier (only its plugins + its tags shown)
    btn.addEventListener('click', () => { try { setTier(pair[1]); window.gdbsShowView('plugins', btn); } catch (e) {} });
    sec.appendChild(btn);
  });
}

const byKey = new Map();   // tier/id -> manifest, for open-by-id (share/kiosk)
function mount(m) {
  const key = m.tier + '/' + m.id;
  if (mounted.has(key)) return;
  byKey.set(key, m);
  addCard(m);
  mounted.add(key);
}

// ---------- boot ----------
async function loadTier(tier) {
  let files = [];
  try { files = await discover(tier); } catch (e) { return; }
  for (const f of files) {
    try {
      const m = await loadManifest(tier, f);
      if (!valid(m, tier)) { console.warn('[plugins] invalid manifest:', tier, f); continue; }
      mount(m);
    } catch (e) { console.warn('[plugins] failed to load', tier + '/' + f, e); }
  }
}

let booted = false;
async function boot() {
  if (booted) return; booted = true;
  // Warm the engine wasm in the BACKGROUND, do not await it: the catalog cards render from
  // index.json metadata (no wasm), so blocking boot on the ~3.3MB engine binary just delayed the
  // whole plugin UI for the ~93% of sessions that never run a wasm engine. Plugin host.engine()
  // uses a self-initializing worker and the engine tabs have their own initWasm, so this is only a
  // precautionary main-thread warm - fire-and-forget is safe.
  try { Promise.resolve(init()).catch(() => {}); } catch (e) { /* engines optional for pure-JS plugins */ }
  for (const t of TIERS) { loadTier(t); }
}

if (document.readyState === 'complete' || document.readyState === 'interactive') setTimeout(boot, 400);
else window.addEventListener('DOMContentLoaded', () => setTimeout(boot, 400));

// Open a plugin's panel directly by tier+id (used by share/kiosk targets).
// Ensures the tier is loaded, then mounts only that plugin on demand.
async function openById(tier, id) {
  // Kiosk binding: a shared-preview visitor is locked to ONE target. Refuse to
  // open any other plugin so a devtools pivot can't run a different engine or
  // contaminate this token's per-link run count.
  try { if (window.__kioskTarget && window.__kioskTarget !== 'plugin:' + tier + ':' + id) return false; } catch (e) {}
  try { await boot(); } catch (e) {}
  try { await loadTier(tier); } catch (e) {}
  const m = byKey.get(tier + '/' + id);
  if (!m) return false;
  try { setTier(tier); } catch (e) {}
  await openPlugin(m, null);
  return true;
}

window.gdbsPlugins = { boot: boot, reload: () => { mounted.clear(); byKey.clear(); booted = false; boot(); }, openById };
